/**
 * /admin/customers — user management + reseller management (iter114s.76).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from './AdminLayout';
import { UsersTable } from './UsersTable';
import { UserEditModal } from './UserEditModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import { InviteUserModal } from './InviteUserModal';
import { BulkInviteModal } from './BulkInviteModal';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCustomers = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/users/`, { withCredentials: true });
      setUsers(r.data?.users || r.data || []);
    } catch { toast.error('Failed to load users.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const closeResetModal = () => { setShowResetModal(false); setSelectedUser(null); setNewPassword(''); };
  const closeEditModal = () => { setShowEditModal(false); setSelectedUser(null); };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      await axios.post(`${API_URL}/api/users/${selectedUser.id}/reset-password`,
        { new_password: newPassword }, { withCredentials: true });
      toast.success('Password reset.'); closeResetModal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed.'); }
  };
  const handleSaveUser = async (userId, updates) => {
    if (!userId) return;
    setSavingUser(true);
    try {
      await axios.put(`${API_URL}/api/users/${userId}`, updates, { withCredentials: true });
      toast.success('Saved.'); fetchUsers(); closeEditModal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
    finally { setSavingUser(false); }
  };
  const handleDeleteUser = async (u) => {
    // iter114s.91 — Defensive diagnostics. Multiple operator reports of
    // "User not found" toasts after iter114s.88's parameter-shape fix.
    // If `u.id` is falsy we now refuse to send the DELETE at all and
    // surface a clear message instead of relying on the backend 404.
    // On a real 404 we refresh the list because the most likely cause
    // is a stale row — user was already deleted in another tab/session.
    if (!u || !u.id) {
      console.error('handleDeleteUser called with malformed user:', u);
      toast.error('Cannot delete — user record is missing an id. Refresh the page and try again.');
      return;
    }
    if (!window.confirm(`Delete user ${u.email || u.id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/${u.id}`, { withCredentials: true });
      toast.success(`Deleted ${u.email || u.id}.`);
      fetchUsers();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      if (status === 404) {
        toast.warning(`User no longer exists (id=${u.id.substring(0, 8)}…). Refreshing the list.`);
        fetchUsers();
      } else {
        toast.error(`Delete failed: ${detail} (id=${u.id.substring(0, 8)}…)`);
      }
    }
  };
  const handleResendInvite = async (u) => {
    try {
      await axios.post(`${API_URL}/api/users/${u.id}/resend-invite`, {}, { withCredentials: true });
      toast.success('Invite resent.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed.'); }
  };
  const handleChangeBillingMode = async (u, mode) => {
    try {
      await axios.put(`${API_URL}/api/users/${u.id}`, { billing_mode: mode }, { withCredentials: true });
      toast.success('Billing mode updated.'); fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed.'); }
  };

  return (
    <AdminLayout>
      <div className="max-w-screen-2xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#14281D]" data-testid="admin-customers-heading">Customers</h1>
          <p className="text-sm text-[#1F3D2C]/70">User accounts, invites, role + reseller management, password resets.</p>
        </header>
        <UsersTable
          users={users}
          currentUserId={user?.id}
          loading={loading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onResetPassword={(u) => { setSelectedUser(u); setShowResetModal(true); }}
          onDeleteUser={handleDeleteUser}
          onEditUser={(u) => { setSelectedUser(u); setShowEditModal(true); }}
          onInviteUser={() => setShowInviteModal(true)}
          onBulkInvite={() => setShowBulkInviteModal(true)}
          onResendInvite={handleResendInvite}
          onChangeBillingMode={handleChangeBillingMode}
        />
      </div>
      <ResetPasswordModal open={showResetModal} user={selectedUser} newPassword={newPassword}
        onChangePassword={setNewPassword} onConfirm={handleResetPassword} onClose={closeResetModal} />
      <UserEditModal open={showEditModal} user={selectedUser} saving={savingUser}
        onSave={handleSaveUser} onClose={closeEditModal} />
      <InviteUserModal open={showInviteModal} onClose={() => setShowInviteModal(false)} onCreated={fetchUsers} />
      <BulkInviteModal open={showBulkInviteModal} onClose={() => setShowBulkInviteModal(false)} onCreated={fetchUsers} />
    </AdminLayout>
  );
};

export default AdminCustomers;
